import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Card, Button, Badge, Select } from '../../components/ui/Kit';
import { useToast } from '../../components/ui/Toast';
import Modal from '../../components/ui/Modal';
import InviteUserModal from './InviteUserModal';
import DeleteUserModal from './DeleteUserModal';
import UserPickerDropdown from '../../components/Settings/UserPickerDropdown';
import SafeIcon from '../../common/SafeIcon';
import Spinner from '../../components/ui/Spinner';
import * as FiIcons from 'react-icons/fi';
import { api, supabase } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { AGENCY_ROLE_OPTIONS, roleOptionLabel } from '../../lib/roleOptions';
import {
  invalidateUsersDirectory,
  resolveDisplayName,
  useUsersDirectory,
} from '../../lib/usersDirectory';

const { FiUserPlus, FiChevronDown, FiUser, FiEdit2, FiLink, FiTrash2 } = FiIcons;

/** Matches Settings RoleGuard MANAGEMENT_ROLES and user_connections RLS. */
const CONNECTION_MANAGE_ROLES = ['manager', 'admin', 'super_admin'];

const CONNECTION_TYPE_OPTIONS = [
  { label: 'Spouse', value: 'spouse' },
  { label: 'Assistant', value: 'assistant' },
  { label: 'Partner', value: 'partner' },
];

const CONNECTION_TYPE_LABELS = {
  spouse: 'Spouse',
  assistant: 'Assistant',
  partner: 'Partner',
};

function canManageConnections(role) {
  return CONNECTION_MANAGE_ROLES.includes(role);
}

function connectionOtherUserId(conn, userId) {
  if (!conn || !userId) return null;
  if (conn.user_id_a === userId) return conn.user_id_b;
  if (conn.user_id_b === userId) return conn.user_id_a;
  return null;
}

function connectionOtherName(conn, userId) {
  const otherId = connectionOtherUserId(conn, userId);
  if (!otherId) return null;
  if (conn.user_id_a === userId) {
    return conn.user_b_name || resolveDisplayName(otherId) || 'Unknown';
  }
  return conn.user_a_name || resolveDisplayName(otherId) || 'Unknown';
}

function connectionsForUser(connections, userId) {
  if (!userId) return [];
  return (connections || []).filter(
    (c) => c.user_id_a === userId || c.user_id_b === userId,
  );
}

function userDisplayName(users, id) {
  if (!id) return '—';
  const fromDir = resolveDisplayName(id);
  if (fromDir) return fromDir;
  const u = users.find((x) => x.id === id);
  return u?.full_name?.trim() || u?.email || '—';
}

function isAdminRole(role) {
  return role === 'admin' || role === 'super_admin';
}

const RESEND_COOLDOWN_MS = 60_000;

function formatLastInvited(lastInvitedAt) {
  if (!lastInvitedAt) return null;
  const d = new Date(lastInvitedAt);
  if (Number.isNaN(d.getTime())) return null;
  return `Last invited ${formatDistanceToNow(d, { addSuffix: true })}`;
}

function neverCompletedSignup(u) {
  // Invites are active immediately; "never logged in" is the pending-invite signal.
  return u?.first_login_at === null;
}

function ToggleSwitch({ checked, onChange, disabled, id }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      id={id}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? 'bg-primary' : 'bg-gray-200'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function RoleDropdown({ userId, currentRole, onRoleChange, disabled }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
      >
        <span>{roleOptionLabel(currentRole)}</span>
        <SafeIcon icon={FiChevronDown} className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute z-20 top-full mt-1 left-0 bg-white rounded-lg shadow-lg border border-gray-200 min-w-[130px] py-1">
          {AGENCY_ROLE_OPTIONS.map((role) => (
            <button
              key={role.value}
              type="button"
              onClick={() => { onRoleChange(userId, role.value); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${role.value === currentRole ? 'text-primary font-semibold' : 'text-gray-700'}`}
            >
              {role.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ConnectionTypeBadge({ type }) {
  const label = CONNECTION_TYPE_LABELS[type] || type;
  return (
    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-violet-100 text-violet-700">
      {label}
    </span>
  );
}

function ManageUserModal({
  user,
  agencyUsers,
  isOpen,
  onClose,
  onSaved,
  connections = [],
  canEditConnections = false,
  onConnectionsChanged,
}) {
  const { addToast } = useToast();
  const [form, setForm] = useState({
    is_marketing_director: false,
    is_licensed: false,
    trainer_id: null,
    marketing_director_id: null,
  });
  const [saving, setSaving] = useState(false);
  const [linkUserId, setLinkUserId] = useState(null);
  const [linkType, setLinkType] = useState('spouse');
  const [linking, setLinking] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    if (!user) return;
    setForm({
      is_marketing_director: !!user.is_marketing_director,
      is_licensed: !!user.is_licensed,
      trainer_id: user.trainer_id || null,
      marketing_director_id: user.marketing_director_id || null,
    });
    setLinkUserId(null);
    setLinkType('spouse');
  }, [user]);

  const activeUsers = useMemo(
    () => agencyUsers.filter((u) => u.is_active !== false && u.id !== user?.id),
    [agencyUsers, user?.id],
  );

  const marketingDirectors = useMemo(
    () => activeUsers.filter((u) => u.is_marketing_director === true),
    [activeUsers],
  );

  const userConnections = useMemo(
    () => connectionsForUser(connections, user?.id),
    [connections, user?.id],
  );

  const linkedIds = useMemo(() => {
    const ids = new Set([user?.id].filter(Boolean));
    userConnections.forEach((c) => {
      ids.add(c.user_id_a);
      ids.add(c.user_id_b);
    });
    return ids;
  }, [userConnections, user?.id]);

  const pickerAllowlist = useMemo(
    () => activeUsers.filter((u) => !linkedIds.has(u.id)),
    [activeUsers, linkedIds],
  );

  const handleSave = async () => {
    if (!user?.id) return;
    setSaving(true);
    try {
      const updated = await api.users.updateProfile(user.id, {
        trainer_id: form.trainer_id || null,
        marketing_director_id: form.marketing_director_id || null,
        is_marketing_director: !!form.is_marketing_director,
        is_licensed: !!form.is_licensed,
      });
      onSaved(updated);
      addToast({ type: 'success', title: 'User updated' });
      onClose();
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Failed to update user',
        message: err?.message || 'Try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddConnection = async () => {
    if (!user?.id || !linkUserId) {
      addToast({ type: 'error', title: 'Pick a user to connect' });
      return;
    }
    setLinking(true);
    try {
      await api.userConnections.create({
        userIdA: user.id,
        userIdB: linkUserId,
        connectionType: linkType,
      });
      setLinkUserId(null);
      setLinkType('spouse');
      addToast({ type: 'success', title: 'Connection added' });
      await onConnectionsChanged?.();
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not add connection',
        message: err?.message || 'Try again.',
      });
    } finally {
      setLinking(false);
    }
  };

  const handleRemoveConnection = async (connectionId) => {
    if (!connectionId) return;
    setRemovingId(connectionId);
    try {
      await api.userConnections.remove(connectionId);
      addToast({ type: 'success', title: 'Connection removed' });
      await onConnectionsChanged?.();
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not remove connection',
        message: err?.message || 'Try again.',
      });
    } finally {
      setRemovingId(null);
    }
  };

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Manage — ${user.full_name || user.email}`} className="max-w-md">
      <div className="space-y-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_marketing_director}
            onChange={(e) => setForm((f) => ({ ...f, is_marketing_director: e.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="text-sm font-medium text-gray-700">Is Marketing Director</span>
        </label>

        <div className="flex items-center justify-between gap-3">
          <div>
            <label htmlFor="manage-user-licensed" className="text-sm font-medium text-gray-700">
              Licensed
            </label>
            <p className="text-xs text-gray-400 mt-0.5">
              Show on the public website About page (requires an active public profile).
            </p>
          </div>
          <ToggleSwitch
            id="manage-user-licensed"
            checked={form.is_licensed}
            onChange={(v) => setForm((f) => ({ ...f, is_licensed: v }))}
            disabled={saving}
          />
        </div>

        <UserPickerDropdown
          label="Assign Trainer (optional)"
          source="directory"
          users={activeUsers}
          value={form.trainer_id}
          onChange={(id) => setForm((f) => ({ ...f, trainer_id: id }))}
          placeholder="Select trainer…"
        />

        <UserPickerDropdown
          label="Assign Marketing Director (optional)"
          source="directory"
          users={marketingDirectors}
          value={form.marketing_director_id}
          onChange={(id) => setForm((f) => ({ ...f, marketing_director_id: id }))}
          placeholder="Select marketing director…"
        />

        {canEditConnections && (
          <div className="pt-4 border-t border-gray-100 space-y-3">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiLink} className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">Connections</h3>
            </div>
            <p className="text-xs text-gray-400">
              Spouse / Assistant / Partner links grant reciprocal read access to each other&apos;s leads and related records.
            </p>

            {userConnections.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No connections yet.</p>
            ) : (
              <ul className="space-y-2">
                {userConnections.map((conn) => {
                  const otherName = connectionOtherName(conn, user.id);
                  return (
                    <li
                      key={conn.id}
                      className="flex items-center justify-between gap-2 rounded-md border border-gray-100 bg-gray-50 px-3 py-2"
                    >
                      <div className="min-w-0 flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900 truncate">{otherName}</span>
                        <ConnectionTypeBadge type={conn.connection_type} />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={removingId === conn.id || linking || saving}
                        onClick={() => handleRemoveConnection(conn.id)}
                        className="text-red-600 hover:bg-red-50 shrink-0"
                      >
                        {removingId === conn.id ? (
                          <Spinner className="text-red-500" />
                        ) : (
                          <>
                            <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5 mr-1" />
                            Remove
                          </>
                        )}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="rounded-md border border-dashed border-gray-200 p-3 space-y-3">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Add Connection</p>
              <UserPickerDropdown
                label="Other user"
                source="directory"
                users={pickerAllowlist}
                value={linkUserId}
                onChange={setLinkUserId}
                placeholder="Select user…"
                disabled={linking || saving}
              />
              <Select
                label="Type"
                value={linkType}
                onChange={(e) => setLinkType(e.target.value)}
                options={CONNECTION_TYPE_OPTIONS}
                disabled={linking || saving}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!linkUserId || linking || saving}
                onClick={handleAddConnection}
              >
                {linking ? <><Spinner className="mr-2" />Adding…</> : 'Add Connection'}
              </Button>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving || linking}>Cancel</Button>
          <Button type="button" onClick={handleSave} disabled={saving || linking}>
            {saving ? <><Spinner className="mr-2" />Saving…</> : 'Save changes'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function UserTableRow({
  u,
  users,
  connections = [],
  currentUser,
  updatingId,
  resendingId,
  inviteCooldownUntil = {},
  isArchived,
  onRoleChange,
  onManage,
  onArchive,
  onUnarchive,
  onToggleActive,
  onDelete,
  onResendInvite,
}) {
  const canDelete = isAdminRole(currentUser?.role) && u.id !== currentUser?.id;
  const showResendInvite =
    !isArchived && isAdminRole(currentUser?.role) && neverCompletedSignup(u);
  const resendCooling = showResendInvite && Date.now() < (inviteCooldownUntil[u.id] || 0);
  const lastInvitedLabel = formatLastInvited(u.last_invited_at);
  const rowConnections = connectionsForUser(connections, u.id);

  return (
    <tr
      key={u.id}
      className={`transition-colors ${
        isArchived
          ? 'bg-amber-50/60 opacity-60 hover:bg-amber-50'
          : `bg-white hover:bg-gray-50 ${!u.is_active ? 'opacity-60' : ''}`
      }`}
    >
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-secondary text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
            {u.full_name?.charAt(0) || 'U'}
          </div>
          <div>
            <p className="font-medium text-gray-900 text-sm flex items-center gap-2 flex-wrap">
              <span>
                {u.full_name}
                {u.id === currentUser?.id && <span className="ml-2 text-[10px] text-gray-400 font-normal">(you)</span>}
              </span>
              {u.is_marketing_director && (
                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700">
                  MD
                </span>
              )}
            </p>
            <p className="text-xs text-gray-400">{u.email}</p>
            {rowConnections.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {rowConnections.map((conn) => (
                  <span
                    key={conn.id}
                    className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] text-violet-800"
                    title={`${CONNECTION_TYPE_LABELS[conn.connection_type] || conn.connection_type}: ${connectionOtherName(conn, u.id)}`}
                  >
                    <ConnectionTypeBadge type={conn.connection_type} />
                    <span className="truncate max-w-[120px]">{connectionOtherName(conn, u.id)}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        {updatingId === u.id ? (
          <Spinner className="text-primary" />
        ) : (
          <RoleDropdown
            userId={u.id}
            currentRole={u.role}
            onRoleChange={onRoleChange}
            disabled={u.id === currentUser?.id || isArchived}
          />
        )}
      </td>
      <td className="px-6 py-4 text-xs text-gray-600">
        <div className="space-y-0.5">
          <p><span className="text-gray-400">Trainer:</span> {userDisplayName(users, u.trainer_id)}</p>
          <p><span className="text-gray-400">MD:</span> {userDisplayName(users, u.marketing_director_id)}</p>
        </div>
      </td>
      <td className="px-6 py-4">
        <Badge variant={isArchived ? 'default' : u.is_active !== false ? 'success' : 'default'}>
          {isArchived ? 'Archived' : u.is_active !== false ? 'Active' : 'Inactive'}
        </Badge>
      </td>
      <td className="px-6 py-4 text-right">
        <div className="flex items-center justify-end gap-2 flex-wrap">
          {isArchived ? (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={updatingId === u.id}
                onClick={() => onUnarchive(u.id)}
              >
                Unarchive
              </Button>
              {canDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={updatingId === u.id}
                  onClick={() => onDelete(u)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  Delete
                </Button>
              )}
            </>
          ) : (
            <>
              {showResendInvite && (
                <div className="flex flex-col items-end gap-0.5">
                  <Button
                    variant="outline"
                    size="sm"
                    isLoading={resendingId === u.id}
                    disabled={
                      resendingId === u.id ||
                      updatingId === u.id ||
                      resendCooling
                    }
                    onClick={() => onResendInvite(u.id)}
                  >
                    Resend Invite
                  </Button>
                  {lastInvitedLabel && (
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">
                      {lastInvitedLabel}
                    </span>
                  )}
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={updatingId === u.id}
                onClick={() => onManage(u)}
              >
                <SafeIcon icon={FiEdit2} className="w-3.5 h-3.5 mr-1" />
                Manage
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={u.id === currentUser?.id || updatingId === u.id}
                onClick={() => onArchive(u)}
                className="text-gray-600"
              >
                Archive
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={u.id === currentUser?.id || updatingId === u.id}
                onClick={() => onToggleActive(u.id, u.is_active !== false)}
                className={u.is_active !== false ? 'text-red-500 border-red-200 hover:bg-red-50' : 'text-green-600 border-green-200 hover:bg-green-50'}
              >
                {u.is_active !== false ? 'Deactivate' : 'Reactivate'}
              </Button>
              {canDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={updatingId === u.id}
                  onClick={() => onDelete(u)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  Delete
                </Button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function UsersTab() {
  const { user } = useAuth();
  const { addToast } = useToast();
  useUsersDirectory();
  const [users, setUsers] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [inviteCooldownUntil, setInviteCooldownUntil] = useState({});
  const [manageUser, setManageUser] = useState(null);
  const [archiveConfirmUser, setArchiveConfirmUser] = useState(null);
  const [selectedUserForDelete, setSelectedUserForDelete] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const cooldownTimers = useRef({});
  const editConnections = canManageConnections(user?.role);

  useEffect(() => () => {
    Object.values(cooldownTimers.current).forEach(clearTimeout);
  }, []);

  const activeUsers = useMemo(
    () => users.filter((u) => !u.archived_at),
    [users],
  );
  const archivedUsers = useMemo(
    () => users.filter((u) => u.archived_at),
    [users],
  );

  const loadConnections = useCallback(async () => {
    try {
      const rows = await api.userConnections.list();
      setConnections(rows || []);
    } catch (err) {
      console.error(err);
      setConnections([]);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadConnections();
  }, [user?.agency_id]);

  const loadUsers = async () => {
    if (!user?.agency_id) {
      setUsers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const agencyUsers = await api.users.listForAgency(user.agency_id);
      let withInviteFields = agencyUsers;
      if (supabase && agencyUsers.length > 0) {
        const { data: extra, error: extraErr } = await supabase
          .from('users')
          .select('id, first_login_at, last_invited_at')
          .eq('agency_id', user.agency_id);
        if (extraErr) {
          console.error(extraErr);
        } else {
          const extraById = Object.fromEntries((extra || []).map((row) => [row.id, row]));
          withInviteFields = agencyUsers.map((u) => ({
            ...u,
            first_login_at: extraById[u.id]?.first_login_at ?? null,
            last_invited_at: extraById[u.id]?.last_invited_at ?? null,
          }));
        }
      }
      setUsers(withInviteFields);
    } catch (err) {
      console.error(err);
      addToast({
        type: 'error',
        title: 'Could not load users',
        message: err?.message || 'Try again in a moment.',
      });
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (userId === user?.id) {
      addToast({ type: 'warning', title: 'Cannot change your own role' });
      return;
    }
    setUpdatingId(userId);
    try {
      await api.users.updateRole(userId, newRole);
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u)));
      invalidateUsersDirectory();
      addToast({ type: 'success', title: 'Role updated' });
    } catch {
      addToast({ type: 'error', title: 'Failed to update role' });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleToggleActive = async (userId, currentActive) => {
    if (userId === user?.id) {
      addToast({ type: 'warning', title: 'Cannot deactivate yourself' });
      return;
    }
    setUpdatingId(userId);
    try {
      await api.users.setActive(userId, !currentActive);
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, is_active: !currentActive } : u)));
      invalidateUsersDirectory();
      addToast({ type: 'success', title: currentActive ? 'User deactivated' : 'User reactivated' });
    } catch {
      addToast({ type: 'error', title: 'Failed to update user' });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleConfirmArchive = async () => {
    if (!archiveConfirmUser?.id || !user?.id) return;
    setArchiving(true);
    setUpdatingId(archiveConfirmUser.id);
    try {
      const updated = await api.users.archive(archiveConfirmUser.id, user.id);
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
      invalidateUsersDirectory();
      addToast({ type: 'success', title: 'User archived' });
      setArchiveConfirmUser(null);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Failed to archive user',
        message: err?.message || 'Try again.',
      });
    } finally {
      setArchiving(false);
      setUpdatingId(null);
    }
  };

  const handleUnarchive = async (userId) => {
    setUpdatingId(userId);
    try {
      const updated = await api.users.unarchive(userId);
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
      invalidateUsersDirectory();
      addToast({ type: 'success', title: 'User unarchived' });
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Failed to unarchive user',
        message: err?.message || 'Try again.',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleInvite = (newUser) => {
    setUsers((prev) => [
      ...prev,
      { ...newUser, first_login_at: newUser.first_login_at ?? null },
    ]);
    invalidateUsersDirectory();
    setShowInvite(false);
  };

  const handleResendInvite = async (userId) => {
    if (!userId || resendingId) return;
    setResendingId(userId);
    try {
      const updated = await api.users.resendInvite(userId);
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, ...updated } : u)),
      );
      addToast({ type: 'success', title: 'Invitation resent' });
      const until = Date.now() + RESEND_COOLDOWN_MS;
      setInviteCooldownUntil((prev) => ({ ...prev, [userId]: until }));
      if (cooldownTimers.current[userId]) clearTimeout(cooldownTimers.current[userId]);
      cooldownTimers.current[userId] = setTimeout(() => {
        setInviteCooldownUntil((prev) => {
          if (prev[userId] !== until) return prev;
          const next = { ...prev };
          delete next[userId];
          return next;
        });
        delete cooldownTimers.current[userId];
      }, RESEND_COOLDOWN_MS);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not resend invite',
        message: err?.message || 'Try again.',
      });
    } finally {
      setResendingId(null);
    }
  };

  const handleUserSaved = (updated) => {
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
    invalidateUsersDirectory();
  };

  const handleDeleted = (result) => {
    setSelectedUserForDelete(null);
    loadUsers();
    invalidateUsersDirectory();
    addToast({
      type: 'success',
      title: 'User deleted',
      message: `${result.leadsTransferred} lead(s) transferred to ${result.newOwnerName}.`,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{users.length} user{users.length !== 1 ? 's' : ''} in your agency</p>
        <div className="flex items-center gap-2">
          {archivedUsers.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="text-gray-500 border-gray-300"
              onClick={() => setShowArchived((v) => !v)}
            >
              {showArchived ? 'Hide Archived' : `Show Archived (${archivedUsers.length})`}
            </Button>
          )}
          <Button size="sm" onClick={() => setShowInvite(true)}>
            <SafeIcon icon={FiUserPlus} className="mr-2 w-4 h-4" />
            Invite User
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3">
            <Spinner size="lg" className="text-primary" />
            <p className="text-sm text-gray-500">Loading users…</p>
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <SafeIcon icon={FiUser} className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-700">No users yet</p>
            <p className="text-xs text-gray-400 mt-1">Invite your first team member to get started.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Team</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {activeUsers.map((u) => (
                <UserTableRow
                  key={u.id}
                  u={u}
                  users={users}
                  connections={connections}
                  currentUser={user}
                  updatingId={updatingId}
                  resendingId={resendingId}
                  inviteCooldownUntil={inviteCooldownUntil}
                  isArchived={false}
                  onRoleChange={handleRoleChange}
                  onManage={setManageUser}
                  onArchive={setArchiveConfirmUser}
                  onToggleActive={handleToggleActive}
                  onDelete={setSelectedUserForDelete}
                  onResendInvite={handleResendInvite}
                />
              ))}
              {showArchived && archivedUsers.length > 0 && (
                <>
                  <tr className="bg-gray-50/80">
                    <td
                      colSpan={5}
                      className="px-6 py-2 text-xs font-medium text-gray-400 uppercase tracking-wide border-t border-gray-200"
                    >
                      Archived Users
                    </td>
                  </tr>
                  {archivedUsers.map((u) => (
                    <UserTableRow
                      key={u.id}
                      u={u}
                      users={users}
                      connections={connections}
                      currentUser={user}
                      updatingId={updatingId}
                      isArchived
                      onRoleChange={handleRoleChange}
                      onManage={setManageUser}
                      onUnarchive={handleUnarchive}
                      onDelete={setSelectedUserForDelete}
                    />
                  ))}
                </>
              )}
            </tbody>
          </table>
        )}
      </Card>

      <InviteUserModal
        isOpen={showInvite}
        onClose={() => setShowInvite(false)}
        onInvite={handleInvite}
        agencyId={user?.agency_id}
      />

      <ManageUserModal
        user={manageUser}
        agencyUsers={users}
        isOpen={!!manageUser}
        onClose={() => setManageUser(null)}
        onSaved={handleUserSaved}
        connections={connections}
        canEditConnections={editConnections}
        onConnectionsChanged={loadConnections}
      />

      <Modal
        isOpen={!!archiveConfirmUser}
        onClose={() => !archiving && setArchiveConfirmUser(null)}
        title={`Archive ${archiveConfirmUser?.full_name || 'user'}?`}
        className="max-w-md"
      >
        <p className="text-sm text-gray-600 mb-6">
          Archive {archiveConfirmUser?.full_name}? They will be hidden from the roster and cannot log in.
          You can unarchive them at any time.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setArchiveConfirmUser(null)} disabled={archiving}>
            Cancel
          </Button>
          <Button onClick={handleConfirmArchive} disabled={archiving}>
            {archiving ? <><Spinner className="mr-2" />Archiving…</> : 'Archive'}
          </Button>
        </div>
      </Modal>

      <DeleteUserModal
        user={selectedUserForDelete}
        currentUser={user}
        onClose={() => setSelectedUserForDelete(null)}
        onDeleted={handleDeleted}
      />
    </div>
  );
}
